import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import { pctToAmount, sumTermPct, type InvoiceTerm } from "@/lib/invoice-plan";

export type { InvoiceTerm };

export const DEFAULT_INVOICE_TERMS: InvoiceTerm[] = [
  { label: "Oppstart", pct: 60 },
  { label: "Levering", pct: 40 },
];

/**
 * Returnerer summen av prosent-feltene. Brukes til 100 %-validering.
 */
export const sumInvoiceTermPct = sumTermPct;

/**
 * True når summen av terminene er nøyaktig 100 % (±0.01).
 */
export function isInvoiceTermsValid(terms: InvoiceTerm[]): boolean {
  return Math.abs(sumInvoiceTermPct(terms) - 100) < 0.01;
}

/**
 * Sett inn en ny termin før siste rad (typisk "Levering"), slik at ekstra
 * milepæl-fakturaer kommer FØR sluttfakturaen — ikke etter. Hvis lista har
 * 0 eller 1 rader plasseres den bare på slutten siden det ikke er noe
 * "etter sluttfakturaen"-konsept å beskytte.
 *
 * VIB-686: Den gamle implementasjonen appendet alltid på slutten, slik at en
 * ekstra milepæl havnet etter "Levering" — det er feil rekkefølge for
 * fakturering.
 */
export function addInvoiceTermBeforeLast(terms: InvoiceTerm[]): InvoiceTerm[] {
  const sum = sumInvoiceTermPct(terms);
  const remaining = Math.max(0, 100 - sum);
  const newRow: InvoiceTerm = { label: "Termin", pct: remaining };
  if (terms.length <= 1) {
    return [...terms, newRow];
  }
  const next = [...terms];
  next.splice(terms.length - 1, 0, newRow);
  return next;
}

interface Props {
  terms: InvoiceTerm[];
  onChange: (next: InvoiceTerm[]) => void;
  /** VIB-2836: ledd som allerede er fakturert — kan ikke endres eller fjernes. */
  isLocked?: (index: number) => boolean;
  /** VIB-2836: kontraktssum — viser kronebeløp per ledd når satt. */
  amountBase?: number;
  /** VIB-2836: overstyrer standard sum-validering (null = gyldig). */
  validationMessage?: string | null;
}

/**
 * Redigerbar liste med faktureringsterminer. Standard er 60/40 (oppstart/
 * levering), men admin kan dele opp i flere — f.eks. 25/25/50 eller
 * 40/30/30. Sum må være 100; UI viser advarsel ellers.
 */
export function InvoiceTermsEditor({
  terms,
  onChange,
  isLocked = () => false,
  amountBase,
  validationMessage,
}: Props) {
  const sum = sumInvoiceTermPct(terms);
  const sumOk = isInvoiceTermsValid(terms);
  const message =
    validationMessage !== undefined
      ? validationMessage
      : sumOk
        ? null
        : "Summen må være 100 % før tilbudet kan lagres.";

  function updateRow(idx: number, patch: Partial<InvoiceTerm>) {
    onChange(terms.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }

  function removeRow(idx: number) {
    onChange(terms.filter((_, i) => i !== idx));
  }

  function addRow() {
    onChange(addInvoiceTermBeforeLast(terms));
  }

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Faktureringsvilkår</Label>
        <span
          className={cn(
            "text-xs tabular-nums",
            message == null ? "text-muted-foreground" : "text-destructive font-medium",
          )}
        >
          Sum: {sum} %
        </span>
      </div>

      <div className="space-y-1.5">
        {terms.map((term, i) => {
          const locked = isLocked(i);
          return (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={term.label}
                disabled={locked}
                aria-label={`Navn på termin ${i + 1}`}
                onChange={(e) => updateRow(i, { label: e.target.value })}
                placeholder="Termin-navn"
                className="h-8 text-sm flex-1"
              />
              <div className="relative w-20">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={term.pct}
                  disabled={locked}
                  aria-label={`Prosent for termin ${i + 1}`}
                  onChange={(e) =>
                    updateRow(i, { pct: Number(e.target.value) || 0 })
                  }
                  className="h-8 text-sm pr-6 text-right tabular-nums"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  %
                </span>
              </div>
              {amountBase != null && (
                <span className="w-24 text-right text-xs tabular-nums text-muted-foreground">
                  {formatNOK(pctToAmount(Number(term.pct) || 0, amountBase))}
                </span>
              )}
              {locked ? (
                <span
                  className="h-8 w-8 flex items-center justify-center text-muted-foreground"
                  title="Fakturert — kan ikke endres"
                  aria-label="Fakturert ledd"
                >
                  <SrIcon name="lock" className="w-3.5 h-3.5" />
                </span>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeRow(i)}
                  disabled={terms.length <= 1}
                  aria-label="Fjern termin"
                >
                  <SrIcon name="x" className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={addRow}
      >
        <SrIcon name="plus" className="w-3 h-3 mr-1" />
        Legg til termin
      </Button>

      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  );
}
